import { Suspense, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import {
  ConnectionBanner,
  DisplayMessage,
  PrefsConflictDialog,
} from '@/shared/components/feedback';
import { AppShell, navGroups, type ConnectionState } from '@/shared/components/layout';
import type { AccountInfo } from '@/shared/components/layout';
import { useAuth } from '@/shared/hooks/useAuth';
import { useAlertasEvaluator } from '@/shared/hooks/useAlertasEvaluator';
import { useCapacidades, useTasaActual } from '@/shared/hooks/useMercado';
import { useNotificaciones } from '@/shared/hooks/useNotificaciones';
import { useT } from '@/shared/i18n/useT';
import { InstallBanner, UpdateBanner } from '@/shared/pwa';
import { useAppSelector } from '@/shared/store/hooks';
import { ErrorBoundary } from './ErrorBoundary';
import { RouteFallback } from './RouteFallback';
import { useRouteEffects } from './useRouteEffects';

/** Fecha ('YYYY-MM-DD') de la última copia guardada de los precios, para «Sin conexión · datos del …». */
function useSnapshotDate(): string | null {
  const savedAt = useAppSelector((s) => s.mercado.ultimos?.savedAt ?? null);
  return savedAt === null ? null : new Date(savedAt).toISOString().slice(0, 10);
}

/**
 * Carcasa de todas las pantallas: enlace «Saltar al contenido», barras (AppShell), avisos de conexión y PWA,
 * evaluación de alertas locales y los diálogos globales. Cada ruta se pinta dentro de su propio límite de
 * error y de carga, así una pantalla rota no tumba la navegación.
 */
export function RootLayout() {
  const { t } = useT();
  const location = useLocation();
  useRouteEffects();
  useAlertasEvaluator();

  const { capacidades } = useCapacidades();
  const { tasa } = useTasaActual();
  const { noLeidas } = useNotificaciones();
  const auth = useAuth();
  const offline = useAppSelector((s) => s.ui.offline);
  const apiReachable = useAppSelector((s) => s.ui.apiReachable);
  const snapshotDate = useSnapshotDate();

  const authEnabled = capacidades.auth.habilitada;
  const isAdmin = auth.user?.rol === 'admin';
  const groups = useMemo(
    () => navGroups({ authEnabled, signedIn: auth.isAuthenticated, isAdmin }),
    [authEnabled, auth.isAuthenticated, isAdmin],
  );
  const account: AccountInfo | null = !authEnabled
    ? null
    : auth.user
      ? {
          status: 'auth',
          nombre: auth.user.nombre,
          email: auth.user.email,
          isAdmin,
          onSignOut: () => void auth.logout(),
        }
      : { status: 'anon' };
  const connection: ConnectionState = offline ? 'offline' : apiReachable ? 'online' : 'apiDown';

  return (
    <>
      <a href="#contenido" className="skip-link">
        {t('nav.skipToContent')}
      </a>
      <AppShell
        groups={groups}
        bcv={tasa}
        unread={noLeidas}
        account={account}
        connection={connection}
        banner={
          <>
            <ConnectionBanner snapshotDate={snapshotDate} />
            <UpdateBanner className="mx-auto max-w-[1200px] rounded-none border-x-0" />
            <InstallBanner className="mx-auto max-w-[1200px] rounded-none border-x-0" />
          </>
        }
      >
        <ErrorBoundary resetKey={location.pathname}>
          <Suspense fallback={<RouteFallback />}>
            <Outlet />
          </Suspense>
        </ErrorBoundary>
      </AppShell>
      <DisplayMessage />
      <PrefsConflictDialog />
    </>
  );
}
